import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { useContentMap } from '../content.js';
import { DEFINED_HOLDS } from '../lib/hold-codes.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { ServicingVerificationRequired } from '../components/ServicingVerificationGuard.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { ACCOUNT_HOLDS_TEMPLATES } from '../email-templates/templates.js';

/**
 * SCRN 42 — View Account Holds and Comments (CCF-79).
 *
 * Faithful port of the customer's Figma "view account holds" design: an account
 * selector (name + last 4 digits), the 3-column holds table (numbering / hold
 * code / comments, max 3 per account) and the "Defined Account Holds and
 * Indicators" reference list. The shared agent banner lives in the app shell,
 * so this screen renders only the `<main>` content.
 *
 * The account may be pre-selected via the `?account=` query param (from the
 * Dashboard "View Holds" link). Holds are fetched from the Teller-ID-gated BFF
 * (`POST /api/accounts/holds`); the account list from `POST /api/customers/accounts`.
 *
 * Issue #454 (@dylan219 2026-07-22): when this screen is reached FROM a medium-
 * or high-security page (the "Hold Information" cross-nav entry, which sets
 * `location.state.fromSecurity` + `returnTo`), the customer is only IDENTIFIED
 * (medium not yet completed), so the medium-gated `/accounts/holds` /
 * `/customers/accounts` reads 403. In that flow the screen instead reads via the
 * IDENTIFIED-gated twins (`/security/holds` + `/security/accounts`) so the agent
 * can view holds pre-medium, and it shows a single "Back to security" control
 * that returns to the ORIGINATING security page (no dashboard / process-menu
 * exit) — mirroring #237 (Customer Notes) and #358 (Check Transactions). On the
 * normal Dashboard path the medium-gated reads are UNCHANGED.
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
}

interface HoldRow {
  Reference?: string;
  Code?: string;
  Comments?: string;
  Amount?: number;
  PlacedDate?: string;
  Reason?: string;
}

interface HoldsResult {
  AccountNumber?: string;
  Holds?: HoldRow[];
  TotalHeld?: number;
}

/** Accessible account selector, faithful to the export's listbox pattern. */
function AccountSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor="account-selection"
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        Select account
      </label>
      <div className="relative">
        <button
          id="account-selection"
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : 'Select'}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label="Select account"
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function AccountHoldsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  // #637 inc 14 — the resolved content-catalogue map, threaded into the
  // hold-removal email templates so their editable wording (subject/title stems,
  // fixed sentences and field labels) reflects any admin reword; absent-key
  // defaults keep the output unchanged.
  const contentMap = useContentMap();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Issue #454 (@dylan219 2026-07-22): whether this screen was reached from a
  // security page's "Hold Information" cross-nav entry, and which security page
  // to return to. Set once at mount from the navigation state — a security page
  // sets `{ fromSecurity: true, returnTo: '<its own route>' }`. In that flow the
  // customer is only IDENTIFIED (pre-medium), so we read via the identified-
  // gated `/security/*` twins and offer only a "Back to security" exit; any
  // other entry (Dashboard link, direct nav) leaves it null and the medium
  // servicing path is unchanged.
  const locationState = location.state as {
    fromSecurity?: boolean;
    returnTo?: string;
  } | null;
  const securityReturn = useRef<{ returnTo: string } | null>(
    locationState?.fromSecurity && locationState.returnTo
      ? { returnTo: locationState.returnTo }
      : null,
  );
  const fromSecurity = !!securityReturn.current;

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState(
    searchParams.get('account') ?? '',
  );
  const [holds, setHolds] = useState<HoldRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Issue #222 part 1: reaching this screen from a pre-verification security
  // page, the servicing accounts call is refused (server 403). Rather than an
  // empty account dropdown + a raw "Could not load accounts." error, show the
  // "complete security first" panel and route the agent back to security.
  const [verificationRequired, setVerificationRequired] = useState(false);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route and forwards the in-scope account context.
  const onProcessSelect = makeProcessMenuHandler(navigate, selectedAccount);

  // Load the customer's accounts for the selector (CCF-79: name + last 4).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        // #454: identified-gated twin when reached from a security page, so the
        // account list loads pre-medium instead of 403'ing.
        const {
          data,
          error: apiError,
          response,
        } = fromSecurity
          ? await api.POST('/security/accounts', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber },
            })
          : await api.POST('/customers/accounts', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber },
            });
        if (cancelled) return;
        if (response.status === 403) {
          // Issue #222 part 1: on the NORMAL (non-security) path, pre-verification
          // the servicing accounts call is refused — show the "complete security
          // first" panel rather than the empty dropdown + "Could not load
          // accounts." error. In the #454 security flow the identified twin does
          // not 403, so this branch is not reached there.
          setVerificationRequired(true);
          return;
        }
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // Load holds whenever an account is selected.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext || !selectedAccount) return;
      setLoading(true);
      setError('');
      setLoaded(false);
      try {
        // #454: identified-gated twin when reached from a security page.
        const {
          data,
          error: apiError,
          response,
        } = fromSecurity
          ? await api.POST('/security/holds', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber, accountNumber: selectedAccount },
            })
          : await api.POST('/accounts/holds', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber, accountNumber: selectedAccount },
            });
        if (cancelled) return;
        if (apiError || !data) {
          setError(
            response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not load account holds (${response.status}).`,
          );
          return;
        }
        const result = data as unknown as HoldsResult;
        setHolds(result.Holds ?? []);
        setLoaded(true);
      } catch {
        if (!cancelled) setError('Request failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, selectedAccount]);

  const options = accounts.map((a) => ({
    value: a.AccountNumber,
    // Issue #543: label with the account's friendly product name (gateway
    // `AccountDescription`, carried as `Nickname` — e.g. "Investment") via the
    // shared helper, matching the dashboard tiles (#380) and Transfer Between
    // Accounts dropdown, rather than the generic account-type bucket ("Deposit
    // Account"). Issue #612: use the shared `accountOptionLabel` (`<name>
    // <last4>`, no hyphen) so this selector matches every other account-list
    // dropdown — the standalone hyphen here was a holds-only inconsistency.
    label: accountOptionLabel(a),
  }));

  // Issue #222 part 1: pre-verification the servicing call is refused (403) —
  // render the "complete security first" panel rather than the empty dropdown +
  // "Could not load accounts." error.
  if (verificationRequired) {
    return <ServicingVerificationRequired />;
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="holds-heading"
    >
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        {/* Issue #454 (@dylan219 2026-07-22): when reached from a security page
            (pre-medium) the ONLY exit is back to the originating security screen
            — the customer is not yet verified (medium), so the agent must not
            step off into the dashboard from here. Otherwise the normal
            "Back to customer dashboard" link is shown. */}
        {fromSecurity ? (
          <a
            href={securityReturn.current!.returnTo}
            onClick={(e) => {
              e.preventDefault();
              navigate(securityReturn.current!.returnTo, {
                state: { restoreSecurityRun: true },
              });
            }}
            className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
          >
            &lt; Back to security
          </a>
        ) : (
          <a
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
          >
            &lt; Back to customer dashboard
          </a>
        )}

        {/* Email templates (#254, mirroring #441/#442): the account-holds email
            templates (R/N hold-removal + Uniscreens usage) are consolidated into
            a single top-right dropdown selector in the page header, from which
            the agent picks one. Each pops the source template prefilled
            best-effort from the in-context customer name/CIF and the selected
            account number, delivered as a formatted downloadable Outlook draft
            (`.eml`, #434) where the template provides `buildHtml`, else a
            plain-text `mailto:` draft. Nothing is sent from the app: the agent
            completes + sends the draft in Outlook. (Replaces the previous button
            row buried under the holds table — per issue #617, matching the
            approved Figma design + the sibling screens.) */}
        <EmailTemplateMenu
          templates={ACCOUNT_HOLDS_TEMPLATES}
          context={{
            customerName: customer.name,
            cif: customer.cif,
            accountNumber: selectedAccount,
            // #637 inc 14 — carry the resolved catalogue map so the R/N
            // hold-removal templates render any admin-reworded wording (text
            // only).
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="holds-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-8"
        >
          View account holds
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). Issue #454: suppressed in the security flow
            (pre-medium) so the agent's only exit is back to the security
            screen. */}
        {!fromSecurity && (
          <ProcessMenu
            options={PROCESS_MENU_OPTIONS}
            onSelect={onProcessSelect}
          />
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Account selector (CCF-79). */}
      <div className="max-w-[900px] mb-6">
        <div className="w-[400px]">
          <AccountSelect
            value={selectedAccount}
            options={options}
            onChange={setSelectedAccount}
          />
        </div>
      </div>

      {selectedAccount && (
        <div className="max-w-[900px]">
          {/* Account holds table — 3 columns, max 3 holds (CCF-79). */}
          <div className="mb-8 border-2 border-[#dad7d3] bg-white overflow-x-auto">
            <table className="w-full border-collapse" role="table">
              <thead>
                <tr className="bg-[#f3f4f6]">
                  <th className="text-left px-4 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] w-[25%]">
                    Account holds
                  </th>
                  <th className="text-left px-4 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] w-[10%]">
                    Hold
                  </th>
                  <th className="text-left px-4 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-[#dad7d3]">
                    Comments
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-2 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : holds.length > 0 ? (
                  holds.map((h, i) => (
                    <tr
                      key={h.Reference ?? i}
                      className="hover:bg-[#f9fafb] transition-colors"
                      aria-label={`Account hold ${i + 1}`}
                    >
                      <td className="px-4 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        Account hold {i + 1}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {(h.Code ?? '').trim()}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                        {/* Comments column shows the per-hold comment — the
                            gateway `account/holds/details` `HoldDescriptionN`
                            field, surfaced by the BFF adapter as the hold's
                            `Comments`/`Reason` (#465; populates when a comment
                            exists, #500). #524: when the gateway carries NO
                            comment for the hold (`HoldDescriptionN` empty —
                            verified live on staging), the cell stays BLANK;
                            substituting the catalogue description here
                            misreports a hold whose host "Further description"
                            is genuinely empty. */}
                        {(h.Comments ?? h.Reason ?? '').trim()}
                      </td>
                    </tr>
                  ))
                ) : (
                  loaded && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-2 text-[14px] font-['Arial',sans-serif] text-[#6b7280] border-b border-[#dad7d3]"
                      >
                        No holds on this account.
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Defined Account Holds and Indicators reference (CCF-79). */}
          <div className="mt-8">
            <h2 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
              Defined Account Holds and Indicators
            </h2>
            <div className="border-2 border-[#dad7d3] bg-white">
              <table className="w-full border-collapse" role="table">
                <tbody>
                  {Array.from(
                    { length: Math.ceil(DEFINED_HOLDS.length / 2) },
                    (_, rowIdx) => {
                      const left = DEFINED_HOLDS[rowIdx * 2];
                      const right = DEFINED_HOLDS[rowIdx * 2 + 1];
                      if (!left) return null;
                      return (
                        <tr key={left[0]} className="border-b border-[#dad7d3]">
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] w-[5%] border-r border-[#dad7d3]">
                            {left[0]}
                          </td>
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c] w-[45%] border-r-2 border-[#dad7d3]">
                            {left[1]}
                          </td>
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] w-[5%] border-r border-[#dad7d3]">
                            {right ? right[0] : ''}
                          </td>
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c] w-[45%]">
                            {right ? right[1] : ''}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
